import React, { useState } from 'react';
import { CheckCircle2, Loader2, RotateCcw, XCircle, Award } from 'lucide-react';
import { hcApi, hcError } from './api';

const Question = ({ q, index, value, onChange }) => {
  const toggle = (opt) => {
    const cur = Array.isArray(value) ? value : [];
    onChange(cur.includes(opt) ? cur.filter((o) => o !== opt) : [...cur, opt]);
  };
  const base = 'w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#922A8C]/40';
  return (
    <fieldset className="rounded-2xl border border-gray-100 bg-[#FBF7FB] p-5" data-testid={`hc-quiz-question-${q.id}`}>
      <legend className="sr-only">Question {index}</legend>
      <p className="font-semibold text-[#363636] mb-3">{index}. {q.question}</p>
      {q.type === 'single' && q.options.map((o) => (
        <label key={o} className="flex items-center gap-3 py-1.5 cursor-pointer">
          <input type="radio" name={q.id} checked={value === o} onChange={() => onChange(o)} className="w-4 h-4 accent-[#922A8C]" data-testid={`hc-quiz-option-${q.id}-${o}`} />
          <span>{o}</span>
        </label>
      ))}
      {q.type === 'multi' && (
        <>
          <p className="text-xs text-gray-500 -mt-2 mb-2">Select all that apply.</p>
          {q.options.map((o) => (
            <label key={o} className="flex items-center gap-3 py-1.5 cursor-pointer">
              <input type="checkbox" checked={Array.isArray(value) && value.includes(o)} onChange={() => toggle(o)} className="w-4 h-4 accent-[#922A8C]" data-testid={`hc-quiz-option-${q.id}-${o}`} />
              <span>{o}</span>
            </label>
          ))}
        </>
      )}
      {q.type === 'numeric' && (
        <input type="number" inputMode="decimal" value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={`${base} max-w-xs`} data-testid={`hc-quiz-input-${q.id}`} />
      )}
      {q.type === 'text' && (
        <textarea rows={3} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={base} data-testid={`hc-quiz-input-${q.id}`} />
      )}
    </fieldset>
  );
};

const Result = ({ result, onRetake }) => (
  <div className={`rounded-2xl p-6 flex gap-4 ${result.passed ? 'bg-green-50 border border-green-200' : 'bg-[#F6EAF5] border border-[#922A8C]/20'}`} data-testid="hc-quiz-result">
    {result.passed ? <CheckCircle2 className="w-8 h-8 text-green-600 shrink-0" /> : <XCircle className="w-8 h-8 text-[#922A8C] shrink-0" />}
    <div>
      <div className="text-xl font-extrabold text-[#363636]" data-testid="hc-quiz-score">
        {result.graded ? `You scored ${result.score}%` : 'Thanks for your answers!'}
      </div>
      <p className="text-gray-600 mt-1">
        {result.passed
          ? (result.graded ? 'You passed this quiz. Great work!' : 'This quiz is complete.')
          : `You need ${result.passing_grade}% to pass. Review the lessons and try again. You can retake it as many times as you need.`}
      </p>
      <button onClick={onRetake} className="mt-4 inline-flex items-center gap-2 h-10 px-5 rounded-xl border-2 border-[#922A8C] text-[#922A8C] font-semibold hover:bg-white" data-testid="hc-quiz-retake">
        <RotateCcw className="w-4 h-4" /> {result.passed ? 'Retake Quiz' : 'Try Again'}
      </button>
    </div>
  </div>
);

export const HcQuiz = ({ courseId, lessonId, quiz, onPassed }) => {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [retaking, setRetaking] = useState(false);

  if (!quiz?.available) return <p className="mt-5 text-gray-500">This quiz isn't available right now.</p>;

  const best = quiz.best_attempt;
  const showPrior = !result && !retaking && best;
  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const { data } = await hcApi.post(`/courses/${courseId}/lessons/${lessonId}/quiz`, { answers });
      setResult(data);
      if (data.passed) onPassed(data);
    } catch (err) {
      setError(hcError(err));
    } finally {
      setSubmitting(false);
    }
  };
  const retake = () => { setResult(null); setAnswers({}); setRetaking(true); };

  return (
    <div className="mt-5 space-y-4" data-testid="hc-quiz">
      <div className="flex flex-wrap gap-3 text-sm text-gray-500">
        <span>{quiz.questions.length} question{quiz.questions.length === 1 ? '' : 's'}</span>
        {quiz.graded && quiz.passing_grade != null && <span>· Passing score: {quiz.passing_grade}%</span>}
        {quiz.attempts > 0 && <span>· Attempts: {quiz.attempts + (result ? 1 : 0)}</span>}
      </div>
      {showPrior && (
        <div className="rounded-2xl bg-[#F6EAF5] p-5 flex gap-3 items-start" data-testid="hc-quiz-prior">
          <Award className="w-6 h-6 text-[#922A8C] shrink-0" />
          <div>
            <p className="font-semibold text-[#363636]">{best.passed ? (best.graded ? `You passed with ${best.score}%.` : 'You completed this quiz.') : `Your best score so far is ${best.score}%.`}</p>
            <button onClick={retake} className="mt-2 text-[#922A8C] font-semibold hover:underline" data-testid="hc-quiz-start-retake">{best.passed ? 'Retake quiz' : 'Try again'}</button>
          </div>
        </div>
      )}
      {result && <Result result={result} onRetake={retake} />}
      {!result && !showPrior && (
        <form onSubmit={submit} className="space-y-4" data-testid="hc-quiz-form">
          {quiz.questions.map((q, i) => (
            <Question key={q.id} q={q} index={i + 1} value={answers[q.id]} onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))} />
          ))}
          {error && <p className="text-sm text-red-600" role="alert" data-testid="hc-quiz-error">{error}</p>}
          <button type="submit" disabled={submitting} className="h-12 px-8 rounded-xl bg-[#922A8C] text-white font-semibold flex items-center gap-2 hover:bg-[#7a2275] disabled:opacity-60" data-testid="hc-quiz-submit">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Submit Quiz
          </button>
        </form>
      )}
    </div>
  );
};
